/**
 * Users Management Page
 * Enterprise-grade user administration interface.
 * 
 * Features:
 * - Dashboard with stats cards (Google Admin Console style)
 * - User list with filters, search, and pagination
 * - Quick actions (suspend, unlock, reset password)
 * - Invite new users via email
 * 
 * Design Patterns:
 * - Meta: Large-scale user management
 * - Amazon IAM: Role-based permissions
 * - Google Admin: Enterprise SSO patterns
 * 
 * AS9100D Compliance:
 * - Full audit trail support
 * - Role-based access control
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserPlus, Search, Filter, RefreshCw, 
  MoreHorizontal, Lock, Unlock, Ban, UserCheck,
  KeyRound, LogOut, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Mail, X,
  Shield, AlertCircle, Clock, CheckCircle2, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { useUsers } from '../hooks/useUsers';
import InviteUserModal from '../components/users/InviteUserModal';
import { 
  USER_ROLES, 
  STATUS_CONFIG, 
  getRoleConfig, 
  getStatusConfig,
  formatRelativeTime 
} from '../services/userService';
import { useAuth } from '../contexts/AuthContext';

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color, subtitle, isLoading }) => (
  <Card className={`p-4 border-l-4 ${color}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-[#101957] mt-1">
          {isLoading ? '—' : value}
        </p>
        {subtitle && (
          <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
        )}
      </div>
      <div className={`p-3 rounded-full bg-slate-50`}>
        <Icon className="w-5 h-5 text-slate-600" />
      </div>
    </div>
  </Card>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <span className="text-[10px]">{config.icon}</span>
      {config.label}
    </span>
  );
};

// Role Badge Component
const RoleBadge = ({ role }) => {
  const config = getRoleConfig(role);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// User Avatar Component
const UserAvatar = ({ firstName, lastName, email }) => {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || email?.[0]?.toUpperCase() || '?';
  return (
    <div className="w-9 h-9 rounded-full bg-[#101957]/10 flex items-center justify-center text-[#101957] font-medium text-sm">
      {initials}
    </div>
  );
};

// Sortable Table Header
const SortableHeader = ({ field, label, currentSort, onSort }) => {
  const isActive = currentSort.sort_by === field;
  return (
    <button
      className="flex items-center gap-1 hover:text-[#101957] transition-colors"
      onClick={() => onSort(field)}
    >
      {label}
      {isActive ? (
        currentSort.sort_order === 'asc' ? 
          <ArrowUp className="w-3 h-3" /> : 
          <ArrowDown className="w-3 h-3" />
      ) : (
        <ArrowUpDown className="w-3 h-3 text-slate-300" />
      )}
    </button>
  );
};

// Confirmation Dialog
const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel, confirmVariant, onConfirm, isLoading }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[400px]">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant={confirmVariant || 'default'}
          onClick={onConfirm}
          disabled={isLoading}
          className={confirmVariant === 'destructive' ? '' : 'bg-[#101957] hover:bg-[#101957]/90 text-white'}
        >
          {isLoading ? 'Processing...' : confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  // User management hook
  const {
    users,
    stats,
    total,
    totalPages,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    handleSort,
    isLoading,
    isStatsLoading,
    actionLoading,
    inviteUser,
    resendInvitation,
    suspendUser,
    reactivateUser,
    unlockUser,
    deactivateUser,
    forceLogoutAll,
    refresh,
  } = useUsers();
  
  // Modal states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  
  // Search debounce
  const [searchInput, setSearchInput] = useState('');
  
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      updateFilters({ search: value });
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [updateFilters]);
  
  // Action handlers
  const handleAction = (action, user) => {
    const actions = {
      suspend: {
        title: `Suspend ${user.full_name}?`,
        description: 'This user will not be able to log in. All active sessions will be terminated.',
        confirmLabel: 'Suspend User',
        confirmVariant: 'destructive',
        onConfirm: () => suspendUser(user.user_id),
      },
      reactivate: {
        title: `Reactivate ${user.full_name}?`,
        description: 'This user will be able to log in again.',
        confirmLabel: 'Reactivate',
        onConfirm: () => reactivateUser(user.user_id),
      },
      unlock: {
        title: `Unlock ${user.full_name}?`,
        description: 'This will reset the failed login counter and allow the user to log in.',
        confirmLabel: 'Unlock Account',
        onConfirm: () => unlockUser(user.user_id),
      },
      deactivate: {
        title: `Deactivate ${user.full_name}?`,
        description: 'This will soft-delete the user. They can be reactivated later.',
        confirmLabel: 'Deactivate',
        confirmVariant: 'destructive',
        onConfirm: () => deactivateUser(user.user_id),
      },
      forceLogout: {
        title: `Force logout ${user.full_name}?`,
        description: 'All active sessions will be terminated immediately.',
        confirmLabel: 'Force Logout',
        confirmVariant: 'destructive',
        onConfirm: () => forceLogoutAll(user.user_id),
      },
      resendInvite: {
        title: `Resend invitation to ${user.email}?`,
        description: 'A new invitation email will be sent.',
        confirmLabel: 'Resend',
        onConfirm: () => resendInvitation(user.user_id),
      },
    };
    
    setConfirmDialog({ ...actions[action], userId: user.user_id });
  };
  
  const executeConfirmAction = async () => {
    if (confirmDialog?.onConfirm) {
      try {
        await confirmDialog.onConfirm();
        setConfirmDialog(null);
      } catch (err) {
        // Error handled by hook
      }
    }
  };
  
  // Get action loading state
  const getActionLoading = (userId) => {
    if (!actionLoading) return false;
    return actionLoading.includes(userId) || actionLoading === 'invite';
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#101957]">User Management</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isLoading}
            data-testid="users-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setInviteModalOpen(true)}
            className="bg-[#101957] hover:bg-[#101957]/90 text-white"
            data-testid="users-invite-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
        <StatsCard
          title="Total Users"
          value={stats?.total || 0}
          icon={Users}
          color="border-l-[#101957]"
          isLoading={isStatsLoading}
        />
        <StatsCard
          title="Active"
          value={stats?.active || 0}
          icon={CheckCircle2}
          color="border-l-green-500"
          subtitle={stats?.total ? `${Math.round((stats.active / stats.total) * 100)}%` : ''}
          isLoading={isStatsLoading}
        />
        <StatsCard
          title="Invited"
          value={stats?.invited || 0}
          icon={Mail}
          color="border-l-blue-500"
          subtitle="Awaiting response"
          isLoading={isStatsLoading}
        />
        <StatsCard
          title="Locked"
          value={stats?.locked || 0}
          icon={Lock}
          color="border-l-red-500"
          subtitle="Failed logins"
          isLoading={isStatsLoading}
        />
        <StatsCard
          title="Suspended"
          value={stats?.suspended || 0}
          icon={Ban}
          color="border-l-orange-500"
          subtitle="Admin action"
          isLoading={isStatsLoading}
        />
      </div>

      {/* Filters Bar */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={handleSearchChange}
              className="pl-10"
              data-testid="users-search-input"
            />
          </div>

          {/* Status Filter */}
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) => updateFilters({ status: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-[140px]" data-testid="users-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${config.color.split(' ')[0]}`} />
                    {config.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Role Filter */}
          <Select
            value={filters.role || 'all'}
            onValueChange={(value) => updateFilters({ role: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-[180px]" data-testid="users-role-filter">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.values(USER_ROLES).map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                clearFilters();
                setSearchInput('');
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="w-4 h-4 mr-1" />
              Clear filters
            </Button>
          )}

          {/* Results count */}
          <div className="ml-auto text-sm text-slate-500">
            {total} user{total !== 1 ? 's' : ''} found
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[250px]">
                <SortableHeader
                  field="full_name"
                  label="User"
                  currentSort={filters}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead>
                <SortableHeader
                  field="role"
                  label="Role"
                  currentSort={filters}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead>
                <SortableHeader
                  field="status"
                  label="Status"
                  currentSort={filters}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead>Department</TableHead>
              <TableHead>
                <SortableHeader
                  field="last_login_at"
                  label="Last Login"
                  currentSort={filters}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
                    <span className="text-slate-500">Loading users...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="w-10 h-10 text-slate-300" />
                    <span className="text-slate-500">No users found</span>
                    {hasActiveFilters && (
                      <Button
                        variant="link"
                        onClick={() => {
                          clearFilters();
                          setSearchInput('');
                        }}
                        className="text-[#101957]"
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow 
                  key={user.user_id}
                  className="hover:bg-slate-50/50 cursor-pointer group"
                  data-testid={`user-row-${user.user_id}`}
                  onClick={() => navigate(`/admin/user/${user.user_id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        firstName={user.first_name}
                        lastName={user.last_name}
                        email={user.email}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 group-hover:text-[#101957] transition-colors">{user.full_name}</p>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={user.role} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={user.status} />
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {user.department || '—'}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {formatRelativeTime(user.last_login_at)}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={getActionLoading(user.user_id)}
                          data-testid={`user-actions-${user.user_id}`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {/* View Profile */}
                        <DropdownMenuItem onClick={() => navigate(`/admin/user/${user.user_id}`)}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Profile
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        
                        {/* Conditional actions based on status */}
                        {user.status === 'invited' && (
                          <DropdownMenuItem onClick={() => handleAction('resendInvite', user)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Resend Invitation
                          </DropdownMenuItem>
                        )}
                        
                        {user.status === 'locked' && (
                          <DropdownMenuItem onClick={() => handleAction('unlock', user)}>
                            <Unlock className="w-4 h-4 mr-2" />
                            Unlock Account
                          </DropdownMenuItem>
                        )}
                        
                        {user.status === 'active' && (
                          <>
                            <DropdownMenuItem onClick={() => handleAction('forceLogout', user)}>
                              <LogOut className="w-4 h-4 mr-2" />
                              Force Logout
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        
                        {['active', 'locked'].includes(user.status) && (
                          <DropdownMenuItem onClick={() => handleAction('suspend', user)}>
                            <Ban className="w-4 h-4 mr-2" />
                            Suspend User
                          </DropdownMenuItem>
                        )}
                        
                        {['suspended', 'deactivated'].includes(user.status) && (
                          <DropdownMenuItem onClick={() => handleAction('reactivate', user)}>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Reactivate
                          </DropdownMenuItem>
                        )}
                        
                        {user.status !== 'invited' && user.user_id !== currentUser?.user_id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleAction('deactivate', user)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <AlertCircle className="w-4 h-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} users
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1 || isLoading}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                      disabled={isLoading}
                      className={page === pageNum ? 'bg-[#101957]' : ''}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages || isLoading}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Invite Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        onInvite={inviteUser}
        isLoading={actionLoading === 'invite'}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
        title={confirmDialog?.title}
        description={confirmDialog?.description}
        confirmLabel={confirmDialog?.confirmLabel}
        confirmVariant={confirmDialog?.confirmVariant}
        onConfirm={executeConfirmAction}
        isLoading={confirmDialog && getActionLoading(confirmDialog.userId)}
      />
    </div>
  );
};

export default UsersPage;
